import { useState } from "react";
import { Server, ChevronDown, Loader2 } from "lucide-react";
import { api } from "./api";

const ROWS = [
  ["whmReachable", "WHM server reachable"], ["tls", "Verified HTTPS (TLS)"], ["authentication", "Authentication"],
  ["requiredPermissions", "7 required permissions"],
  ["listAccounts", "List accounts"], ["listPackages", "List packages"], ["createAccount", "Create account capability"],
  ["cpanelApi", "cPanel API capability"], ["createUserSession", "create-user-session privilege"], ["sslRead", "SSL read (optional)"],
];
const TONE = {
  PASS: "text-emerald-300 bg-emerald-500/10 ring-emerald-500/30",
  FAIL: "text-rose-300 bg-rose-500/10 ring-rose-500/30",
  UNKNOWN: "text-amber-300 bg-amber-500/10 ring-amber-500/30",
};
const Badge = ({ v, testid }) => <span data-testid={testid} className={`rounded-md px-2 py-0.5 text-xs ring-1 ${TONE[v] || TONE.UNKNOWN}`}>{v}</span>;

const Result = ({ r }) => (
  <div className="space-y-2" data-testid="hosting-check-result">
    <p className="text-sm flex items-center gap-2">Overall <Badge v={r.overall} testid="hosting-check-overall" /></p>
    <p className="text-sm text-slate-300" data-testid="hosting-check-message">{r.message}</p>
    <ul className="space-y-1">
      {ROWS.map(([k, label]) => (
        <li key={k} className="flex items-center justify-between text-sm"><span className="text-slate-300">{label}</span><Badge v={r.checks[k]} testid={`hosting-check-${k}`} /></li>
      ))}
    </ul>
    {r.missingPrivileges.length > 0 && <p className="text-sm text-rose-300" data-testid="hosting-check-missing">Missing required permissions: {r.missingPrivileges.join(", ")}</p>}
    <p className="text-xs text-slate-400 flex items-center gap-2">Optional ssl-info permission <Badge v={r.optionalPrivileges["ssl-info"]} testid="hosting-check-ssl-info" /></p>
    <p className="text-xs text-slate-500">Read-only check · {new Date(r.checkedAt).toLocaleString()}</p>
  </div>
);

export const HostingCheck = () => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const run = async () => {
    setBusy(true); setErr("");
    try { setRes(await api.hostingCheck()); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <section className="rounded-2xl bg-slate-900/70 ring-1 ring-slate-800 p-4" data-testid="hosting-diagnostics">
      <button onClick={() => setOpen((v) => !v)} data-testid="hosting-diagnostics-toggle" className="w-full flex items-center justify-between text-[11px] uppercase tracking-wide text-slate-400">
        <span className="flex items-center gap-1.5"><Server className="h-3.5 w-3.5" /> Hosting diagnostics</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-slate-500">Runs a read-only check of the WHM connection. Nothing is created or changed.</p>
          <button onClick={run} disabled={busy} data-testid="hosting-check-button" className="flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm text-slate-100 hover:bg-slate-700 transition-colors disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Check hosting connection
          </button>
          {err && <p className="text-sm text-rose-300" data-testid="hosting-check-error">{err}</p>}
          {res && <Result r={res} />}
        </div>
      )}
    </section>
  );
};
